import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from '../../i18n';
import { Send, Remove, Update } from '../../services/service.api';
import { toast } from 'react-toastify';
import { Plus, Trash2, Edit2, X, Package } from 'lucide-react';
import ConfirmModal from '../../components/component.confirmModal';

function formatFCFA(n) {
  return `${Number(n || 0).toLocaleString('fr-FR')} FCFA`;
}

function ProductModal({ product, onClose, onSave, t }) {
  const [form, setForm] = useState({
    libelle: product?.libelle || '',
    quantite: product?.quantite ?? 0,
    prix_unitaire_vente: product?.prix_unitaire_vente || '',
  });

  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.libelle) return toast.error(t.common.required);
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {product ? t.products.edit : t.products.add}
          </h2>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">
                {t.products.libelle} <span className="required">*</span>
              </label>
              <input
                className="form-control"
                value={form.libelle}
                onChange={e => setForm({ ...form, libelle: e.target.value })}
                placeholder="Ex: Tissu wax, Sucre 50kg..."
                required
              />
            </div>
            <div className="grid-2">
              {/* Quantité : visible seulement en mode édition */}
              {product && (
                <div className="form-group">
                  <label className="form-label">{t.products.quantite}</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.quantite}
                    onChange={e => setForm({ ...form, quantite: e.target.value })}
                    placeholder="0"
                  />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">{t.products.prixVente}</label>
                <input
                  type="number"
                  min="0"
                  className="form-control"
                  value={form.prix_unitaire_vente}
                  onChange={e => setForm({ ...form, prix_unitaire_vente: e.target.value })}
                  placeholder="0"
                />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>
              {t.common.cancel}
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Enregistrement...' : t.common.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Products() {
  const { t } = useTranslation();
  const token = useSelector(s => s.userReducer.token);
  const activeCompany = useSelector(s => s.userReducer.activeCompany);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 30;

  
  const abortControllerRef = useRef(null);

  const fetchProducts = useCallback(async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoading(true);
    try {
      const companyId = activeCompany?.id || '';
      const headers = { authorization: "Bearer " + token, 'Content-Type': 'application/json' };
      if (companyId) headers['X-Company-Id'] = companyId;
      const params = new URLSearchParams();
      if (companyId) params.append('company_id', companyId);
      params.append('_t', Date.now());
      const qs = params.toString() ? `?${params.toString()}` : '';
      
      const API_URL = process.env.REACT_APP_API_URL || "https://savaninvests-backend.onrender.com";
      const response = await fetch(`${API_URL}/products${qs}`, { headers, signal });
      const res = await response.json();

      if (!signal.aborted && Array.isArray(res)) {
        setProducts(res);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        // silently fail
      }
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [token, activeCompany?.id]);


  
  useEffect(() => {
    setProducts([]); // Clear to prevent ghost data
    fetchProducts();
  }, [fetchProducts]);


  

  const handleSave = async (form) => {
    try {
      let res;
      if (editProduct) {
        res = await Update(`/products/${editProduct.id}`, form, token);
      } else {
        res = await Send('/products', form, true, token);
      }
      if (res?.error) { toast.error(res.error); return; }
      toast.success(editProduct ? 'Produit mis à jour' : 'Produit créé');
      setShowModal(false);
      setEditProduct(null);
      fetchProducts();
    } catch { toast.error('Erreur'); }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await Remove(`/products/${deleteId}`, token);
      toast.success('Produit supprimé');
      setDeleteId(null);
      fetchProducts();
    } catch { toast.error('Erreur'); }
  };

  const filtered = products.filter(p =>
    String(p.libelle || '').toLowerCase().includes(search.toLowerCase())
  );
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="page-content page-enter">
      <div className="page-header">
        <div>
          <h1>{t.products.title}</h1>
          <p>{products.length} produit(s) enregistré(s)</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditProduct(null); setShowModal(true); }}>
          <Plus size={16} /> {t.products.add}
        </button>
      </div>

      {/* Search */}
      <div className="search-wrapper mb-4" style={{ maxWidth: 320 }}>
        <Package size={16} />
        <input
          className="form-control search-input"
          placeholder={t.common.search}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="card">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="skeleton" style={{ height: 48, marginBottom: 8, borderRadius: 8 }} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📦</div>
          <h3>{t.products.noProducts}</h3>
          <p>Cliquez sur "Ajouter un produit" pour commencer</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>{t.products.libelle}</th>
                <th>{t.products.quantite}</th>
                <th>{t.products.prixVente}</th>
                <th>{t.products.valeurStock}</th>
                <th>Statut</th>
                <th>{t.common.actions}</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map(p => {
                const valeur = Number(p.quantite || 0) * Number(p.prix_unitaire_vente || 0);
                const stockStatus = Number(p.quantite) === 0 ? 'rupture'
                  : Number(p.quantite) < 5 ? 'faible' : 'ok';
                return (
                  <tr key={p.id}>
                    <td><strong>{p.libelle}</strong></td>
                    <td className="number-format">
                      <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>
                        {Number(p.quantite || 0)}
                      </span>
                    </td>
                    <td className="number-format">{formatFCFA(p.prix_unitaire_vente)}</td>
                    <td className="number-format">{formatFCFA(valeur)}</td>
                    <td>
                      {stockStatus === 'rupture' && <span className="badge badge-danger">Rupture</span>}
                      {stockStatus === 'faible' && <span className="badge badge-gold">⚠ Faible</span>}
                      {stockStatus === 'ok' && <span className="badge badge-success">En stock</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => { setEditProduct(p); setShowModal(true); }}
                          title={t.common.edit}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => setDeleteId(p.id)}
                          title={t.common.delete}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
          <button className="btn btn-outline btn-sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>← Préc.</button>
          <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Page {currentPage} / {totalPages}</span>
          <button className="btn btn-outline btn-sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Suiv. →</button>
        </div>
      )}

      {showModal && (
        <ProductModal
          product={editProduct}
          onClose={() => { setShowModal(false); setEditProduct(null); }}
          onSave={handleSave}
          t={t}
        />
      )}

      <ConfirmModal
        isOpen={!!deleteId}
        title="Supprimer ce produit"
        message="Êtes-vous sûr de vouloir supprimer ce produit du catalogue ?"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}
